import React from 'react';
import { TimelineCard } from '../shared/TimelineCard';
import { ProgressBar } from '../shared/ProgressBar';
import { motion } from 'framer-motion';
export const TimelineSection = () => {
  const steps = [{
    number: 1,
    title: 'Trajectory Planning & Value Assessment',
    description: 'Define your career direction and comp expectations.',
    details: 'Get clear on key company criteria and assess your value based on experience, skillset, and location. Set confident comp targets and learn to communicate them.'
  }, {
    number: 2,
    title: 'Job Search Roadmap & Time Management',
    description: 'Create a customized schedule to accelerate your prep.',
    details: 'Craft a weekly roadmap across DS/A, System Design, Domain Fundamentals, and Behavioral/Leadership prep. Build an achievable yet aspirational calendar.'
  }, {
    number: 3,
    title: 'Resume Reconstruction',
    description: 'Rewrite your resume to land Senior/Staff interviews.',
    details: 'Fully rewrite your resume to reflect seniority and quantify impact. Create multiple versions if targeting different roles, optimized for ATS.'
  }, {
    number: 4,
    title: 'LinkedIn SEO & Strategy',
    description: 'Turn your profile into a recruiter magnet.',
    details: 'Rebuild your LinkedIn using keyword and skill tagging. Increase recruiter reach by 3–10x through SEO tactics.'
  }, {
    number: 5,
    title: 'Company & Networking Strategy',
    description: 'Prioritize companies and referrals for faster traction.',
    details: 'Tier target companies by alignment and opportunity. Build and activate your referral network for high-value interviews.'
  }, {
    number: 6,
    title: 'Personal Narrative Construction',
    description: 'Nail the first question in every interview.',
    details: 'Create and practice your "tell me about yourself" answer. Frame your narrative around Senior/Staff-level readiness.'
  }, {
    number: 7,
    title: 'Interview Story Crafting',
    description: 'Be ready for any behavioral or leadership question.',
    details: 'Write 8-12 stories aligned to key themes. Tag each story by trait and master our 3-part framework for real-time deployment.'
  }, {
    number: 8,
    title: 'Interview Prep & Support',
    description: 'Go in prepared for your most important loops.',
    details: 'Get dedicated prep calls before critical interviews. Review company nuances and adjust strategy for final rounds.'
  }, {
    number: 9,
    title: 'Interview Recording Reviews',
    description: 'Real feedback on real interviews — every week.',
    details: 'Record every round and get detailed coaching to rapidly uncover patterns. Tighten feedback loops at every stage.'
  }, {
    number: 10,
    title: 'Salary Negotiation',
    description: 'Maximize every offer with expert guidance.',
    details: 'Develop negotiation plans, co-write scripts, and roleplay calls. Analyze each offer for risks and upside.'
  }, {
    number: 11,
    title: 'Live Coaching & Weekly Syncs',
    description: 'Consistent feedback and real-time iteration.',
    details: 'Meet 2-3x/week to review progress, highlight wins, and tackle blockers. Stay aligned with strategic priorities.'
  }, {
    number: 12,
    title: 'Text & Slack Access',
    description: 'Direct access to your coach between sessions.',
    details: 'Get quick feedback and support anytime. Perfect for urgent questions or mindset check-ins.'
  }];
  return <section className="w-full bg-gradient-to-br from-[#231232] to-[#1a0d24] py-16 sm:py-24 relative overflow-hidden" data-id="element-268">
      <div className="absolute inset-0 overflow-hidden" data-id="element-269">
        <motion.div animate={{
        y: [0, -20, 0]
      }} transition={{
        duration: 10,
        repeat: Infinity,
        ease: 'linear'
      }} className="absolute inset-0 bg-[radial-gradient(circle_at_center,_#FFC501_1px,_transparent_1px)] bg-[length:40px_40px] opacity-[0.15]" data-id="element-270" />
        <motion.div animate={{
        y: [0, 20, 0]
      }} transition={{
        duration: 15,
        repeat: Infinity,
        ease: 'linear'
      }} className="absolute inset-0 bg-[radial-gradient(circle_at_center,_#DF4425_1px,_transparent_1px)] bg-[length:30px_30px] opacity-[0.1]" data-id="element-271" />
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative" data-id="element-272">
        <div className="text-center mb-12 sm:mb-16" data-id="element-273">
          <h2 className="text-3xl sm:text-4xl font-outfit font-bold mb-4 sm:mb-6 text-white" data-id="element-274">
            The Platinum Roadmap
          </h2>
          <p className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto mb-4" data-id="element-275">
            A Clear Path to $300K+ Roles — With the Coaching to Get You There
          </p>
          <p className="text-base sm:text-lg text-white/60 max-w-3xl mx-auto" data-id="element-276">
            This is the same system that's helped engineers land offers at Meta,
            Snowflake, PayPal, and Lattice. We build a personalized roadmap that
            focuses your time, unlocks interviews fast, and gives you
            play-by-play feedback so you can tighten your process every single
            week.
          </p>
        </div>
        {/* Mobile Timeline */}
        <div className="md:hidden relative max-w-lg mx-auto" data-id="element-277">
          <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#FFC501]/30 via-[#DF4425]/30 to-transparent" data-id="element-278" />
          {steps.map((step, index) => <div key={step.number} className="relative pl-16 mb-8 last:mb-0" data-id="element-279">
              {/* Timeline Node */}
              <div className="absolute left-0 flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-br from-[#FFC501] to-[#DF4425] shadow-lg shadow-[#FFC501]/20" data-id="element-280">
                <span className="text-[#231232] font-outfit font-bold text-xl" data-id="element-281">
                  {step.number}
                </span>
              </div>
              {/* Card Content */}
              <div className="bg-[#231232]/70 backdrop-blur-md border border-[#6805DA]/20 rounded-xl p-6 shadow-lg transition-all duration-300 hover:border-[#FFC501]/40" data-id="element-282">
                <h3 className="font-outfit text-xl font-bold mb-3 text-white" data-id="element-283">
                  {step.title}
                </h3>
                <p className="text-gray-300 mb-4 text-base" data-id="element-284">
                  {step.description}
                </p>
                <p className="text-[#FFC501]/80 text-sm" data-id="element-285">{step.details}</p>
              </div>
              {/* Connector Line */}
              {!index === steps.length - 1 && <div className="absolute left-6 top-12 h-8 w-0.5 bg-gradient-to-b from-[#FFC501]/30 to-transparent" data-id="element-286" />}
            </div>)}
        </div>
        {/* Desktop Timeline */}
        <div className="hidden md:block relative max-w-5xl mx-auto" data-id="element-287">
          <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#FFC501]/30 via-[#DF4425]/30 to-transparent" data-id="element-288" />
          {steps.map((step, index) => <TimelineCard key={step.number} {...step} isLast={index === steps.length - 1} alignment={index % 2 === 0 ? 'left' : 'right'} data-id="element-289" />)}
        </div>
      </div>
      <ProgressBar data-id="element-290" />
    </section>;
};